import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, List } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { API_ROOT } from "../../../constants/api";
import RelatedLinkWithPreview from "../../../components/RelatedLinkWithPreview";

function AddRelatedLinkButton({ parentId }) {
  const dispatch = useDispatch();

  // Find the gallery item that matches this parentId
  // For images, parentId is the URL; for text boxes, parentId is the ID
  const galleryItem = useSelector((state) => {
    if (!parentId) return null;
    const item = state.editGallery.gallery.find(
      (item) =>
        (item.metatype === "image" && item.url === parentId) ||
        (item.metatype === "text" && item.id === parentId)
    );
    return item;
  });

  const relatedLinks = Array.isArray(galleryItem?.related_links) ? galleryItem.related_links : [];

  // Start CLOSED by default
  const [isOpen, setIsOpen] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [localUrl, setLocalUrl] = React.useState("");
  const [localLinkText, setLocalLinkText] = React.useState("");
  const [isFetchingTitle, setIsFetchingTitle] = React.useState(false);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setEditingIndex(null);
      setLocalUrl("");
      setLocalLinkText("");
    }
  };

  const fetchArticleTitle = async (url) => {
    // Only fetch for Daily Bruin URLs
    if (!url || !url.includes('dailybruin.com')) {
      return;
    }

    // Don't fetch if text is already filled
    if (localLinkText && localLinkText.trim()) {
      return;
    }

    setIsFetchingTitle(true);
    try {
      const response = await fetch(`${API_ROOT}/gallery/fetch_article_title?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (data.title) {
        setLocalLinkText(data.title);
      }
    } catch (error) {
      console.error('Failed to fetch article title:', error);
    } finally {
      setIsFetchingTitle(false);
    }
  };

  const handleUrlChange = (newUrl) => {
    setLocalUrl(newUrl);
    
    // Auto-fetch title for Daily Bruin URLs
    if (newUrl && newUrl.trim() && newUrl.includes('dailybruin.com')) {
      fetchArticleTitle(newUrl);
    }
  };

  const handleTextChange = (newText) => {
    setLocalLinkText(newText);
  };

  const handleSave = () => {
    if (!parentId) return;
    
    if (editingIndex !== null) {
      // Update existing link
      dispatch({
        type: "UPDATE_RELATED_LINK",
        payload: {
          id: parentId,
          linkIndex: editingIndex,
          url: localUrl ?? "",
          text: localLinkText ?? "",
        },
      });
    } else {
      // Add new link
      dispatch({
        type: "ADD_RELATED_LINK",
        payload: {
          id: parentId,
          url: localUrl ?? "",
          text: localLinkText ?? "",
        },
      });
    }
    
    // Reset form
    setEditingIndex(null);
    setLocalUrl("");
    setLocalLinkText("");
  };

  const handleEdit = (index) => {
    const link = relatedLinks[index];
    setEditingIndex(index);
    setLocalUrl(link.url || "");
    setLocalLinkText(link.text || "");
  };

  const handleDelete = (index) => {
    if (!parentId) return;
    dispatch({
      type: "DELETE_RELATED_LINK",
      payload: { id: parentId, linkIndex: index },
    });
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setLocalUrl("");
    setLocalLinkText("");
  };

  // Don't render if no parentId
  if (!parentId) {
    return null;
  }

  const hasLinks = relatedLinks.length > 0;

  return (
    <div className="related-link-control">
      <Button
        type="default"
        size="middle"
        onClick={toggleOpen}
        style={{ marginTop: 8, marginBottom: 4 }}
      >
        {isOpen ? "Hide related links" : hasLinks ? `Edit related links (${relatedLinks.length})` : "Add related link"}
      </Button>

      {/* Show preview when links exist but form is closed */}
      {!isOpen && hasLinks && (
        <div style={{ marginTop: 8, marginBottom: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          {relatedLinks.map((link, index) => (
            <p key={index} style={{ margin: index > 0 ? '4px 0 0 0' : '0', fontSize: '12px', color: '#666' }}>
              <strong>Related Link {index + 1}:</strong>{" "}
              <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                {link.text || link.url}
              </a>
            </p>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="related-link-fields" style={{ marginTop: 8 }}>
          {/* List of existing links */}
          {relatedLinks.length > 0 && (
            <List
              size="small"
              dataSource={relatedLinks}
              renderItem={(link, index) => (
                <List.Item
                  actions={[
                    <Button
                      key="edit"
                      size="small"
                      onClick={() => handleEdit(index)}
                      disabled={editingIndex !== null}
                    >
                      Edit
                    </Button>,
                    <Button
                      key="delete"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(index)}
                      disabled={editingIndex !== null}
                    >
                      Delete
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.text || link.url}
                      </a>
                    }
                    description={link.url}
                  />
                </List.Item>
              )}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Add/Edit form */}
          <div style={{ border: '1px solid #d9d9d9', padding: 16, borderRadius: 4 }}>
            <RelatedLinkWithPreview
              label={editingIndex !== null ? "Edit Related Link" : "Add Related Link"}
              url={localUrl}
              text={localLinkText}
              onUrlChange={handleUrlChange}
              onTextChange={handleTextChange}
              isFetchingTitle={isFetchingTitle}
            />

            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button 
                type="primary" 
                size="small" 
                onClick={handleSave}
                disabled={!localUrl && !localLinkText}
                icon={editingIndex === null ? <PlusOutlined /> : null}
              >
                {editingIndex !== null ? "Update Link" : "Add Link"}
              </Button>
              {editingIndex !== null && (
                <Button size="small" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddRelatedLinkButton;
